<template>
  <div class="h-full w-full text-primary">
    <ChatWidget ref="ChatWidgetRef" v-show="ready && widgetType === 'chat'" />
    <div
      class="flex flex-col gap-3 justify-end h-full pb-3 items-end relative"
      v-if="ready && widgetType === 'quick_question' && closeSuggestedQuestions.length"
    >
      <button
        v-for="(question, idx) in closeSuggestedQuestions"
        :key="question"
        @click="onQuickClick(question)"
        type="button"
        class="rounded-bubble-radius bg-quick-suggestion p-4 text-sm font-semibold text-suggestionText shadow-md text-left ring-appBorder relative"
      >
        <button
          @click.stop="closeQuickQuestion"
          v-if="idx === 0"
          class="absolute right-0 -translate-y-1/2 top-0 bg-app p-1 rounded-full text-closeSuggestionsButtonColor"
        >
          <CloseIcon class="w-4 h-4" />
        </button>
        {{ question }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import Emitter from '@/emit.js'
import { renderMarkdown } from '@/converter'
import ChatWidget from '@/components/ChatWidget.vue'
import { useWidget } from '@/widget'
import { useMessages } from '@/message'
import CloseIcon from '@/assets/icons/close.svg'
import { useLocalStorage } from '@vueuse/core'
import { log, uuid } from '@/helpers'
const ChatWidgetRef = ref()
const {
  config,
  options,
  updateValue,
  reset,
  setTheme,
  isOpenGlobal,
  widgetType,
  sendPostMessage,
  updateMenu,
  ready,
  init: initWidget,
  generateMenu,
  setSpeak,
  updateValueConfig,
  widgetLocalStorage
} = useWidget()
const {
  setHistory,
  setInitSuggestionQuestions,
  resetMessages,
  initMessage,
  initSuggestedQuestion,
  pushMessage,
  stateMessages
} = useMessages()

const closeSuggestedQuestions = computed(() =>
  initSuggestedQuestion.value.slice(
    0,
    options.suggestedQuestionQuantity > 2 ? 2 : options.suggestedQuestionQuantity
  )
)
const onQuickClick = (question) => {
  sendPostMessage({ question }, 'open_chat_with_question')
}
const closeQuickQuestion = () => {
  sendPostMessage({}, 'close_quick_question')
}
const initApp = () => {
  initWidget()
  initMessage()
  ready.value = true
  if (widgetLocalStorage.value.open) {
    sendPostMessage({ open_chat: true }, 'open_chat')
  }
}

onMounted(() => {
  const debug = useLocalStorage('x-talk-debug', false, { writeDefaults: false })
  updateValue('debug', debug.value)
  const urlParams = new URLSearchParams(window.location.search)

  if (!urlParams.has('assistantId')) {
    console.error('assistantId is required')
    return
  }

  if (urlParams.has('assistantId')) {
    updateValueConfig('assistantId', urlParams.get('assistantId'))
  }
  setTimeout(() => {
    setSpeak()
  }, 1000)
  window.addEventListener('message', handleMessage)
})
onUnmounted(async () => {
  window.removeEventListener('message', handleMessage)
})

const handleMessage = (event) => {
  if (event.data) {
    if (config.debug) {
      log('MESSAGE', event.data)
    }
    if (event.data && event.data.theme) {
      setTheme(event.data.theme)
    }

    Object.keys(event.data).forEach((key) => {
      if (key === 'config') {
        window.xTalkConfig = event.data[key]
        initApp()
      }
      if (key === 'historyMessages') {
        const convertMessage = event.data[key].map((obj) => {
          let text = obj.content
          text = text.replace(/ - /g, '\n- ')

          return {
            ...obj,
            content: renderMarkdown(text)
          }
        })

        setHistory(convertMessage)
      } else if (key === 'suggestedQuestionCount') {
        const { count, questions } = event.data[key]
        const parsedCount = parseInt(count)
        updateValue('suggestedQuestionQuantity', isNaN(count) ? 1 : count)
        const sliced = questions.slice(0, parsedCount)
        setInitSuggestionQuestions(sliced)
      } else if (key === 'suggestedQuestion') {
        const questions = event.data[key]
        const sliced = questions.slice(0, options.suggestedQuestionQuantity)
        setInitSuggestionQuestions(sliced)
      } else if (key === 'open') {
        Emitter.emit('close', event.data[key])
      } else if (key === 'chat_type') {
        widgetType.value = event.data[key]
      } else if (key === 'send_message') {
        widgetType.value = 'chat'
        Emitter.emit('send_message', event.data[key])
      } else if (key === 'menu') {
        generateMenu(event.data[key])
      } else if (key === 'updateMenu') {
        updateMenu('rodo', event.data[key])
      } else if (key === 'addBotMessage') {
        const ev = event.data[key]
        const id = uuid()
        stateMessages.currentBotMessageId = id
        pushMessage({
          sender: options.assistantName,
          content: ev.content,
          id: id,
          status: 'sent',
          bot: true,
          source: [],
          suggestedQuestions: initSuggestedQuestion.value
        })
      } else {
        updateValue(key, event.data[key])
      }
    })
  }
}

Emitter.on('close', (value) => {
  log('emitter: close')
  isOpenGlobal.value = value
  if (widgetType.value === 'chat') {
    widgetLocalStorage.value.open = value
  }
})
Emitter.on('reset', () => {
  log('emitter: reset')
  reset()
  resetMessages()
  initApp()
})
Emitter.on('resize', (value) => {})
Emitter.on('adaptiveCardAction', (action) => {
  const toSend = {
    ...action,
    data: { ...action.data, product: action.product }
  }

  sendPostMessage(toSend, 'adaptive_card_action')
})
</script>
